import React, { useEffect, useRef } from 'react';
import { ArrowRight, Code, Layers, Rocket, Search, TestTube } from 'lucide-react';
import { siteContent } from '../content/siteContent';

const processIconMap = {
  search: Search,
  layers: Layers,
  code: Code,
  testTube: TestTube,
  rocket: Rocket,
};

const Process = () => {
  const sectionRef = useRef(null);
  const { process } = siteContent;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" ref={sectionRef} className="relative py-32 overflow-hidden bg-gradient-to-b from-transparent via-[#D22527]/5 to-transparent">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-20">
          <h2 className="fade-in-up text-5xl md:text-6xl font-bold text-white mb-6">
            {process.heading[0]}
            <br />
            <span className="bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
              {process.heading[1]}
            </span>
          </h2>
          <p className="fade-in-up text-xl text-gray-400 max-w-3xl mx-auto">{process.intro}</p>
        </div>

        <div className="relative">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {process.steps.map((step, index) => {
              const Icon = processIconMap[step.icon];

              return (
                <div
                  key={step.number}
                  className="fade-in-up group relative"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="process-card p-8 rounded-3xl bg-gradient-to-br from-[#D22527]/5 to-transparent border border-[#D22527]/20 hover:border-[#D22527]/50 transition-all duration-500 hover:scale-105 backdrop-blur-sm h-full">
                    <span className="process-card-sheen-wrap">
                      <span className="process-card-sheen"></span>
                    </span>
                    <div className="absolute -top-4 left-8 w-16 h-16 rounded-2xl bg-gradient-to-br from-[#D22527] to-[#88171A] flex items-center justify-center text-white font-bold text-xl group-hover:scale-110 transition-transform duration-500 group-hover:rotate-6 shadow-lg shadow-[#D22527]/50">
                      {step.number}
                    </div>

                    <div className="mt-8 mb-6">
                      <div className="w-14 h-14 rounded-xl bg-black/50 border border-[#D22527]/30 flex items-center justify-center group-hover:bg-[#D22527]/10 transition-all duration-300">
                        <Icon className={`process-icon ${step.iconAnimation} w-7 h-7 text-[#D22527]`} />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#D22527] transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                  </div>

                  {index < process.steps.length - 1 && (
                    <div className="process-connector hidden lg:flex absolute top-[5.75rem] -right-10 -translate-y-1/2 items-center justify-center">
                      <span className="process-connector-line"></span>
                      <span className="process-connector-core">
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
